import { HelpCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dismissHint, isHintDismissed } from '../../shared/onboarding/onboardingState';
import './FeatureHint.css';

export type FeatureHintProps = {
  hintId: string;
  title: string;
  body: React.ReactNode;
  /** Anchor content (e.g. button) — hint control is placed after children */
  children: React.ReactNode;
  /** Popover horizontal alignment */
  popoverAlign?: 'left' | 'right';
  /**
   * When true, the hint never disappears: only closes the popover (no localStorage).
   * Use for logic users must be able to re-read anytime (e.g. Add row).
   */
  persistent?: boolean;
  /** Wider popover and typography for step lists */
  variant?: 'default' | 'rich';
  /** Replace default "?" trigger (e.g. icon + label) */
  triggerContent?: React.ReactNode;
};

/**
 * "?" (or custom) control next to an element; popover explains the feature.
 * Default: "Got it" hides forever (localStorage). Use persistent for always-available help.
 */
export const FeatureHint: React.FC<FeatureHintProps> = ({
  hintId,
  title,
  body,
  children,
  popoverAlign = 'right',
  persistent = false,
  variant = 'default',
  triggerContent,
}) => {
  const [gone, setGone] = useState(() => !persistent && isHintDismissed(hintId));
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  const handleDismissPopover = useCallback(() => {
    if (persistent) {
      setOpen(false);
      return;
    }
    dismissHint(hintId);
    setGone(true);
    setOpen(false);
  }, [hintId, persistent]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = anchorRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (gone) {
    return <>{children}</>;
  }

  const triggerClasses = [
    'feature-hint-trigger',
    persistent ? 'feature-hint-trigger--persistent' : '',
    triggerContent ? 'feature-hint-trigger--custom' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const popoverClasses = [
    'feature-hint-popover',
    popoverAlign === 'left' ? 'feature-hint-popover--left' : '',
    variant === 'rich' ? 'feature-hint-popover--rich' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span ref={anchorRef} className="feature-hint-anchor">
      {children}
      <button
        type="button"
        className={triggerClasses}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? `feature-hint-${hintId}` : undefined}
        id={`feature-hint-trigger-${hintId}`}
        aria-label={`Help: ${title}. Opens an explanation.`}
        title={persistent ? `Open help — ${title}` : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {triggerContent ?? <span aria-hidden>?</span>}
      </button>
      {open ? (
        <div
          id={`feature-hint-${hintId}`}
          className={popoverClasses}
          role="dialog"
          aria-labelledby={`feature-hint-title-${hintId}`}
          aria-modal="false"
        >
          <div id={`feature-hint-title-${hintId}`} className="feature-hint-popover-title">
            {title}
          </div>
          <div className="feature-hint-popover-body">{body}</div>
          <div className="feature-hint-popover-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={handleDismissPopover}>
              {persistent ? 'Close' : 'Got it'}
            </button>
          </div>
        </div>
      ) : null}
    </span>
  );
};

/** Default icon trigger for persistent “how it works” hints */
export const FeatureHintHelpTrigger: React.FC = () => (
  <>
    <HelpCircle size={15} strokeWidth={2.25} aria-hidden className="feature-hint-trigger-icon" />
    <span className="feature-hint-trigger-text">How</span>
  </>
);
